// @flow
import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import { Button, MessageInputBox, Text, TextBlock } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { format, parse } from 'date-functions';
import { PersonIdContext } from 'bk-web-app';

import type { ClientComment, InternalNote } from './types';

const MessageCardLayout = styled.div`
  background: ${colors.white};
  border-radius: 2px;
  border: 1px solid ${colors.clay};
  margin-bottom: 4px;
  padding: 12px 16px 8px;
`;

const BodyText = styled(TextBlock)`
  overflow-wrap: break-word;
  width: 365px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  // Because the total-ghost buttons in this container have 3px of padding, -3px on the sides aligns
  // their edge with the content of the header
  margin: 0 -3px;
  padding-top: 8px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 8px;
`;

const EditMessage = styled.div`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  text-align: center;
`;

type MessageCardProps = {
  deleteMessage?: () => any,
  editMessage?: (message: string) => any,
  flagMessage?: (flagged: boolean) => any,
  message: ClientComment | InternalNote,
};

type MessageCardState = {
  isEditing: boolean,
};

class MessageCard extends Component<MessageCardProps, MessageCardState> {
  static defaultProps = {
    deleteMessage: undefined,
    editMessage: undefined,
    flagMessage: undefined,
  };

  state = {
    isEditing: false,
  };

  activateEditMode = () => {
    this.setState({ isEditing: true });
  };

  deactivateEditMode = () => {
    this.setState({ isEditing: false });
  };

  handleMessagePost = (messageBody: string): Promise<any> => {
    const { message, editMessage } = this.props;
    if (typeof editMessage !== 'function') {
      return Promise.resolve();
    }
    if (messageBody === message.body) {
      this.deactivateEditMode();
      return Promise.resolve();
    }
    return editMessage(messageBody).then(this.deactivateEditMode);
  };

  render() {
    const { isEditing } = this.state;
    const { deleteMessage, editMessage, flagMessage, message } = this.props;
    const { author, body, createdOn } = message;
    const isFlagged = typeof message.flagged !== 'undefined' ? message.flagged : undefined;
    const isFlaggable = typeof flagMessage === 'function' && typeof isFlagged !== 'undefined';
    const isEditable = typeof editMessage === 'function';
    const isDeletable = typeof deleteMessage === 'function';
    return (
      <MessageCardLayout>
        <CardHeader>
          <Text color={colors.cloud} styleType="small">
            <PersonIdContext.Consumer>
              {personId => (personId === author.id ? 'Me' : author.fullName || 'Unknown')}
            </PersonIdContext.Consumer>
          </Text>
          <Text color={colors.dust} styleType="small">
            {format(parse(createdOn), 'MM/DD/YYYY')}
          </Text>
        </CardHeader>
        {/* The key prop causes the input to reset when the message is successfully updated. When
         * isEditing is false the input is only visually hidden but still renders so that if an
         * attempt to save fails or "Cancel" is pressed the user's edited message is not lost. */}
        <EditMessage key={body} isVisible={isEditing}>
          <MessageInputBox
            data-test-id="message-edit-textarea"
            defaultValue={body}
            onPost={this.handleMessagePost}
          />
          <Button onClick={this.deactivateEditMode} size="small" styleType="total-ghost">
            Cancel
          </Button>
        </EditMessage>
        {!isEditing && (
          <Fragment>
            <BodyText>{body}</BodyText>
            {(isFlaggable || isEditable || isDeletable) && (
              <CardFooter>
                {isFlaggable && (
                  <Button
                    icon={isFlagged ? 'flagFilled' : 'flagUnfilled'}
                    onClick={() =>
                      // $FlowFixMe isFlaggable guarantees flagNote is a function
                      flagMessage(!isFlagged)
                    }
                    size="small"
                    styleType="total-ghost"
                  />
                )}
                {isEditable && (
                  <Button
                    data-test-id="message-edit-button"
                    onClick={this.activateEditMode}
                    size="small"
                    styleType="total-ghost"
                  >
                    Edit
                  </Button>
                )}
                {isDeletable && (
                  <Button onClick={deleteMessage} size="small" styleType="total-ghost">
                    Delete
                  </Button>
                )}
              </CardFooter>
            )}
          </Fragment>
        )}
      </MessageCardLayout>
    );
  }
}

export default MessageCard;
